import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Briefcase, ChevronRight, Calendar, Euro, Ruler, Building2, Compass, Tag } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import { resolveImage, useTaxonomies } from "../lib/realisations";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="border border-gray-200 p-5">
    <div className="overline text-gray-500 mb-1 flex items-center gap-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#E60000]" strokeWidth={2} />}
      {label}
    </div>
    <div className="font-display text-base md:text-lg font-bold">{value}</div>
  </div>
);

export default function RealisationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { categories: CATEGORIES, missions: MISSIONS } = useTaxonomies();
  const categoryLabel = (key) => CATEGORIES.find((c) => c.key === key)?.label || key;
  const missionLabel = (key) => MISSIONS.find((x) => x.key === key)?.label || key;
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/realisations/${slug}`);
        if (!alive) return;
        setProject(data);
        // Fetch related (same category, excluding current)
        const { data: all } = await api.get("/realisations");
        const rel = all
          .filter((r) => r.category === data.category && r.slug !== data.slug)
          .slice(0, 3);
        setRelated(rel);
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) setNotFound(true);
        else console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-400 text-sm tracking-wider uppercase">Chargement…</div>;
  }

  if (notFound || !project) {
    return (
      <div data-testid="realisation-not-found" className="min-h-[60vh] flex flex-col items-center justify-center gap-6 text-center px-6">
        <h1 className="font-display text-4xl font-bold">Réalisation introuvable</h1>
        <p className="text-gray-500">Le projet que vous cherchez n&apos;existe pas ou a été déplacé.</p>
        <button onClick={() => navigate("/realisations")} data-testid="back-to-list" className="bg-[#E60000] hover:bg-[#c40000] text-white px-6 py-3 text-sm tracking-[0.18em] uppercase font-semibold">
          Retour aux réalisations
        </button>
      </div>
    );
  }

  const title = `${project.name} — ${project.location} | ECO-GEST PATRIMOINE`;
  const description = `${project.type}. ${project.description}`.slice(0, 280);
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.name,
    description: project.description,
    image: project.cover?.startsWith("http") ? project.cover : `${baseUrl}${project.cover}`,
    locationCreated: { "@type": "Place", name: project.location },
    creator: { "@type": "Organization", name: "ECO-GEST PATRIMOINE SAS" },
    keywords: project.missions.join(", "),
    url: `${baseUrl}/realisations/${project.slug}`,
  };

  return (
    <div data-testid={`realisation-detail-${project.slug}`}>
      <SEO path={`/realisations/${project.slug}`} title={title} description={description} image={project.cover} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO with cover */}
      <section className="relative h-[60vh] min-h-[420px] bg-[#1A1A1A] text-white overflow-hidden flex items-end">
        <img src={resolveImage(project.cover)} alt={project.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-14 w-full">
          {/* Breadcrumb */}
          <nav className="overline mb-6 text-gray-300 flex items-center gap-2 flex-wrap" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/realisations" className="hover:text-white">Réalisations</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white truncate max-w-[60vw]">{project.name}</span>
          </nav>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {project.category && (
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold bg-[#E60000] text-white px-2.5 py-1">
                {categoryLabel(project.category)}
              </span>
            )}
            {(project.missions || []).map((m) => (
              <span key={m} className="text-[10px] tracking-[0.2em] uppercase font-semibold border border-white/40 text-white px-2.5 py-1">
                {missionLabel(m)}
              </span>
            ))}
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-none">{project.name || "Réalisation"}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-200">
            {project.location && <span className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-[#E60000]" />{project.location}</span>}
            {project.type && <span className="flex items-center gap-2 text-sm"><Briefcase className="w-4 h-4 text-[#E60000]" />{project.type}</span>}
            {project.year && <span className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-[#E60000]" />{project.year}</span>}
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Link to="/realisations" data-testid="back-link" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-gray-600 hover:text-[#E60000] mb-10">
            <ArrowLeft className="w-3 h-3" /> Toutes les réalisations
          </Link>

          {project.description && (
            <Reveal>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">Présentation du projet</h2>
              <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">{project.description}</p>
            </Reveal>
          )}

          {/* Key facts — only render populated ones */}
          <Reveal delay={100}>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
              {project.location && (
                <Fact icon={MapPin} label="Localisation" value={project.location} />
              )}
              {project.category && (
                <Fact icon={Building2} label="Catégorie" value={categoryLabel(project.category)} />
              )}
              {project.year && (
                <Fact icon={Calendar} label="Année" value={project.year} />
              )}
              {project.budget && (
                <Fact icon={Euro} label="Budget travaux" value={project.budget} />
              )}
              {project.surface && (
                <Fact icon={Ruler} label="Surface" value={project.surface} />
              )}
              {project.client && (
                <Fact icon={Briefcase} label="Client" value={project.client} />
              )}
              {project.architect && (
                <Fact icon={Compass} label="Architecte" value={project.architect} />
              )}
              {project.usage && (
                <Fact icon={Tag} label="Usage" value={project.usage} />
              )}
              {(project.extra_fields || []).filter((f) => f.label && f.value).map((f, i) => (
                <Fact key={`extra-${i}`} icon={Tag} label={f.label} value={f.value} />
              ))}
              {(project.missions || []).length > 0 && (
                <Fact icon={Briefcase} label="Missions" value={project.missions.map(missionLabel).join(" · ")} />
              )}
            </div>
          </Reveal>

          {/* Gallery */}
          {(project.gallery || []).filter(Boolean).length > 0 && (
            <Reveal delay={200}>
              <h2 className="font-display text-2xl md:text-3xl font-bold mt-16 mb-6">Galerie</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(project.gallery || []).filter(Boolean).map((g, i) => (
                  <div key={i} className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img loading="lazy" src={resolveImage(g)} alt={`${project.name} — illustration ${i + 1}`} className="img-cover" />
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* CTA Contact */}
      <section className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-3">Un projet similaire à confier&nbsp;?</h3>
            <p className="text-gray-300 max-w-xl">Discutons de votre opération. ECO-GEST PATRIMOINE vous accompagne en Économie de la construction, MOEx et OPC sur l&apos;ensemble du territoire.</p>
          </div>
          <div className="md:text-right">
            <Link to="/contact" data-testid="cta-contact" className="inline-block bg-[#E60000] hover:bg-[#c40000] text-white px-8 py-4 text-sm tracking-[0.2em] uppercase font-semibold">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h3 className="font-display text-2xl md:text-3xl font-bold mb-8">Autres réalisations en {categoryLabel(project.category)}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/realisations/${p.slug}`} data-testid={`related-${p.slug}`} className="group block bg-white border border-gray-200 hover:border-[#E60000] transition-colors">
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img loading="lazy" src={resolveImage(p.cover)} alt={p.name} className="img-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]" />
                  </div>
                  <div className="p-5">
                    <h4 className="font-display text-lg font-bold tracking-tight mb-2">{p.name}</h4>
                    <p className="text-xs text-gray-500">{p.location}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
